import {
  PropertySharingField,
  PropertySharingFieldPart,
  PropertySharingIdentifierPart,
  PropertySharingIdentifierSelector,
} from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  propertyLinkExampleValue,
  propertyLinkParameterKey,
} from '../../../utils/property-link-template.util';
import type { PropertyLinkParameterSelectProps } from '../../../types/property-links.types';

/** Choose an available field or identifier with consistently aligned labels and examples.
 * @param props - Current variable, authorized identifier choices and selection callback.
 * @returns The shared searchable select configured for URL variables.
 */
export function PropertyLinkParameterSelect({
  parameter,
  identifiers,
  disabled,
  onChange,
}: PropertyLinkParameterSelectProps) {
  const { t } = useI18n();
  const choices: (PropertySharingFieldPart | PropertySharingIdentifierPart)[] =
    [
      ...Object.values(PropertySharingField)
        .filter((field) => field !== PropertySharingField.MERCADO_LIBRE_CODE)
        .map((field) => ({
          kind: PropertySharingFieldPart.kind.PROPERTY,
          field,
        })),
      ...identifiers.map((selector) => ({
        kind: PropertySharingIdentifierPart.kind.IDENTIFIER,
        selector,
        field: PropertySharingIdentifierPart.field.VALUE,
      })),
    ];
  if (
    parameter?.kind === PropertySharingIdentifierPart.kind.IDENTIFIER &&
    !choices.some(
      (choice) =>
        propertyLinkParameterKey(choice) === propertyLinkParameterKey(parameter)
    )
  ) {
    choices.push(parameter);
  }
  return (
    <SearchableSelect
      disabled={disabled}
      placeholder={t('propertyLinks.chooseSource')}
      value={parameter ? propertyLinkParameterKey(parameter) : undefined}
      options={choices.map((choice) => ({
        value: propertyLinkParameterKey(choice),
        content:
          choice.kind === PropertySharingFieldPart.kind.PROPERTY
            ? t(`propertyLinks.field.${choice.field}`)
            : `${choice.selector.label ?? t('propertyLinks.unlabelled')} · ${choice.selector.system} · ${t(choice.selector.kind === PropertySharingIdentifierSelector.kind.PROPERTY_NATIVE ? 'propertyLinks.nativeIdentifier' : 'propertyLinks.customIdentifier')}`,
      }))}
      renderOption={(option) => {
        const choice = choices.find(
          (item) => propertyLinkParameterKey(item) === option.value
        );
        return (
          <div className="min-w-0 w-full space-y-1 text-left">
            <Text size="small" className="!text-left">
              {option.content}
            </Text>
            {choice && (
              <Text
                size="tiny"
                color="secondary"
                className="!text-left break-words whitespace-normal"
              >
                {t(
                  choice.kind === PropertySharingFieldPart.kind.PROPERTY
                    ? `propertyLinks.sourceHelp.${choice.field}`
                    : 'propertyLinks.sourceHelp.identifier'
                )}{' '}
                · {propertyLinkExampleValue(choice)}
              </Text>
            )}
          </div>
        );
      }}
      onChange={(value) =>
        onChange(
          choices.find(
            (choice) => propertyLinkParameterKey(choice) === value
          ) ?? null
        )
      }
    />
  );
}
